/**
 * Abstract prints — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wall_art`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const abstractPrintsPack: ToilePack = {
    slug: 'abstract-prints',
    category: 'wall-art',
    name: 'Abstract prints',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'arches', label: 'Arches', subject: 'three overlapping arches, one half-circle rising behind the others and a low horizontal bar cutting across their feet'},
        {key: 'stroke', label: 'Brush stroke', subject: 'one wide sweeping stroke across an otherwise empty field, its bristle edges dry and broken where it lifts'},
        {key: 'collage', label: 'Collage', subject: 'a torn-paper collage of four rectangles, their deckled edges overlapping slightly and a single thin diagonal crossing them'},
    ],
    styles: [
        {key: 'matte', label: 'Matte', style: 'printed as flat matte gouache, bold clean shapes with hard edges, no outlines and no gradients', palette: 'muted terracotta, sand and off-white on a warm paper ground', framing: 'The shapes sit centred with a generous empty margin on every side.'},
        {key: 'riso', label: 'Riso', style: 'printed as a two-ink risograph, grainy flat fills and a slight misregistration where the two inks overlap', palette: 'a two-colour ink pairing of deep blue and fluoro coral over an oatmeal ground', framing: 'The composition sits large in the sheet inside a narrow even margin.'},
        {key: 'wash', label: 'Wash', style: 'painted as a loose watercolour wash, soft feathered edges and pigment pooling at the foot of each shape', palette: 'washed sage, clay and bone tones over a cold-pressed paper ground', framing: 'The forms float near the centre with wide quiet space around them.'},
    ],
    directions: ['in green instead', 'as a set of three', 'for a bedroom wall'],
    seo: {
        title: 'Abstract wall art: arches, brush stroke, collage',
        metaDescription: 'Nine abstract prints — arches, brush stroke and collage — in matte, riso or wash finish, matte texture and generous margin, gallery-quiet.',
        h1: 'Abstract wall art: arches, brush stroke and collage',
        tagline: 'Nine prints, three abstract subjects, three finishes each — the print that goes with any sofa.',
        intro: 'Shapes and nothing else: overlapping terracotta arches, a single brush stroke on white, a torn-paper collage. These nine cross those three subjects with a matte finish, a two-tone riso print and a soft watercolor wash — generous margin, gallery-quiet, the print that works over any sofa.',
        useCases: [
            'the wall above a sofa',
            'an entryway or hallway',
            'an office reception wall',
            'a set of three for a large wall',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each print is drawn at A3 proportions with an even margin, so it prints clean at A3 or scales to A4.'},
            {q: 'Can I get a different color palette?', a: 'Yes — the direction field takes a color request, for instance green, and redraws the same shape set.'},
            {q: 'Is there any text in these prints?', a: 'No — these are flat abstract artwork with no lettering, frame or mockup.'},
        ],
    },
}
